import { useEffect, useState, useCallback } from 'react';
import { safeAxios } from '@/services/safeAxios';
import { useWorkflowStore } from '@/state/workflow.state';
import type { ApiPath } from '@/services/apiPath';
import type { DesignAppResult } from '@/types/design-app.types';
import type { ResolvedAssets } from '@/types/api/assets';

// Constants
const POLLING_INTERVAL = 1000;
const MAX_RETRY_ATTEMPTS = 3;

// Types
export type RunStatus = 'COMPLETED' | 'FAILED' | 'CANCELED' | 'RUNNING';

type RunStatusResponse = {
  status: RunStatus;
  progress: number;
  results: DesignAppResult[];
  error: string;
  outputCount?: number;
};

type StatusResponse = {
  runs: Record<string, RunStatusResponse>;
  userRemainingCredits?: number;
  remainingCredits?: number;
  resolvedAssets?: ResolvedAssets;
};

type StatusPollingParams = {
  addCurrentResults: (
    newResults: DesignAppResult[],
    userRemainingCredits?: number,
    workspaceRemainingCredits?: number,
  ) => void;
  statusUrl: ApiPath;
  onDonePolling: () => void;
};

type PollingState = {
  isProcessing: boolean;
  progress: number;
  errorMessage?: string;
};

export const useStatusPolling = ({ addCurrentResults, statusUrl, onDonePolling }: StatusPollingParams) => {
  const [pollingState, setPollingState] = useState<PollingState>({
    isProcessing: false,
    progress: 0,
  });
  const [runIdsToPoll, setRunIdsToPoll] = useState<string[]>([]);
  const [numberOfLoadingResults, setNumberOfLoadingResults] = useState(0);
  const mergeResolvedAssets = useWorkflowStore((state) => state.mergeResolvedAssets);

  /**
   * Handles the status response and updates state accordingly
   */
  const handleStatusResponse = useCallback(
    (response: StatusResponse): { runId: string; outputCount: number }[] => {
      mergeResolvedAssets(response.resolvedAssets ?? {});
      const remainingRuns: { runId: string; outputCount: number }[] = [];
      const { runs, userRemainingCredits, remainingCredits } = response;

      Object.entries(runs).forEach(([runId, status]) => {
        switch (status.status) {
          case 'COMPLETED':
            addCurrentResults(status.results, userRemainingCredits, remainingCredits);
            break;
          case 'FAILED':
            setPollingState((prev) => ({ ...prev, errorMessage: status.error }));
            break;
          case 'RUNNING':
            remainingRuns.push({ runId, outputCount: status.outputCount ?? 0 });
            break;
          // CANCELED status is handled implicitly by not adding to remainingRunIds
        }
      });

      const minProgress = Math.min(...Object.values(runs).map((status) => status.progress));
      setPollingState((prev) => ({ ...prev, progress: minProgress }));

      return remainingRuns;
    },
    [addCurrentResults, mergeResolvedAssets],
  );

  // Polling effect
  useEffect(() => {
    if (!runIdsToPoll.length) return;

    let timeoutId: NodeJS.Timeout;
    const abortController = new AbortController();
    let retryCount = 0;
    let currentRunIds = [...runIdsToPoll];

    const pollStatus = async (runIds: string[]) => {
      if (!runIds.length) return [];

      try {
        const response = await safeAxios.get<StatusResponse>(statusUrl, {
          params: { runIds: runIds.join(',') },
          signal: abortController.signal,
        });
        if (abortController.signal.aborted) return [];

        retryCount = 0;
        return handleStatusResponse(response.data);
      } catch (error) {
        if (abortController.signal.aborted) return [];

        setPollingState((prev) => ({
          ...prev,
          errorMessage: error instanceof Error ? error.message : 'An unexpected error occurred',
        }));
        retryCount++;
        return runIds.map((runId) => ({ runId, outputCount: 0 }));
      }
    };

    const poll = async () => {
      if (abortController.signal.aborted) return;
      if (retryCount >= MAX_RETRY_ATTEMPTS) {
        setPollingState((prev) => ({ ...prev, isProcessing: false }));
        onDonePolling();
        return;
      }
      const remainingRuns = await pollStatus(currentRunIds);
      if (abortController.signal.aborted) return;

      currentRunIds = remainingRuns.map((run) => run.runId);
      setNumberOfLoadingResults(remainingRuns.reduce((acc, curr) => acc + curr.outputCount, 0));
      if (currentRunIds.length) {
        timeoutId = setTimeout(() => void poll(), POLLING_INTERVAL);
      } else {
        setPollingState((prev) => ({ ...prev, isProcessing: false }));
        onDonePolling();
      }
    };

    setPollingState((prev) => ({ ...prev, isProcessing: true }));
    void poll();

    return () => {
      abortController.abort();
      clearTimeout(timeoutId);
    };
  }, [runIdsToPoll, statusUrl, handleStatusResponse, onDonePolling]);

  const prepareForRun = useCallback(() => {
    setRunIdsToPoll([]);
    setNumberOfLoadingResults(0);
    setPollingState({ isProcessing: false, progress: 0 });
  }, []);

  /**
   * Starts polling for the given run IDs
   */
  const startPolling = useCallback((runIds: string[]) => {
    if (!runIds?.length) return;

    setPollingState({
      isProcessing: true,
      progress: 0,
      errorMessage: undefined,
    });
    setRunIdsToPoll(runIds);
  }, []);

  return {
    errorMessage: pollingState.errorMessage,
    isProcessing: pollingState.isProcessing,
    numberOfLoadingResults,
    progress: pollingState.progress,
    prepareForRun,
    startPolling,
  };
};
